import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import 'chartjs-adapter-date-fns';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, tap, takeUntil } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { IThematicPerformancePoint } from '../../../../../../core/models/results.model';
import { STAGE_COLORS, STAGE_LEGEND_COLORS, StageName } from '../../../../../../shared/utils/bracket.utils';
import { LoadingProgressComponent, LoadingStage } from '../../../../../../shared/components/loading-progress/loading-progress.component';

// ─── Constants ─────────────────────────────────────────────────────
const SIDENAV_KEY = 'thematic_performance_history';

const LOADING_STAGES: LoadingStage[] = [
  { text: 'Scanning innovators across the theme…', atSeconds: 0 },
  { text: 'Identifying inventions in scope…', atSeconds: 8 },
  { text: 'Selecting the top 50 companies by exposure…', atSeconds: 25 },
  { text: 'Calculating 10 years of portfolio returns…', atSeconds: 40 },
  { text: 'Indexing cumulative returns & mapping S-curve stages…', atSeconds: 150 },
];
const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const DAY_MS = 86_400_000;
const AXIS_PAD_DAYS = 14;
const MIN_ZOOM_RANGE_MS = 30 * DAY_MS;

const COLOR = {
  line: '#000000',
  axisText: '#64748b',
  axisTitle: '#64748b',
  tooltipBg: '#ffffff',
  tooltipTitle: '#64748b',
  tooltipBody: '#0f172a',
  tooltipBorder: 'rgba(15, 23, 42, 0.08)',
  grid: 'rgba(15, 23, 42, 0.05)',
  crosshair: 'rgba(0, 0, 0, 0.45)',
  zoomBoxBg: 'rgba(99, 102, 241, 0.15)',
  zoomBoxBorder: 'rgba(99, 102, 241, 0.6)',
};

const STAGE_SOFT_BG: Record<StageName, string> = {
  Emerging:     'rgba(59, 130, 246, 0.08)',
  Accelerating: 'rgba(34, 197, 94, 0.08)',
  Maturing:     'rgba(234, 179, 8, 0.10)',
  Declining:    'rgba(239, 68, 68, 0.08)',
  'No Stage':   'rgba(156, 163, 175, 0.08)',
};

const STAGE_LABELS: { name: StageName; color: string }[] = (
  ['Emerging', 'Accelerating', 'Maturing', 'Declining', 'No Stage'] as StageName[]
).map(name => ({ name, color: STAGE_LEGEND_COLORS[name] }));

// ─── Date helpers ──────────────────────────────────────────────────
const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
const addDays = (d: Date, days: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c;
};

// ─── Chart.js plugins ──────────────────────────────────────────────
// Paints a white background behind the chart so PNG exports aren't transparent.
const whiteBackgroundPlugin = {
  id: 'whiteBackground',
  beforeDraw(chart: Chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

// Dashed vertical crosshair that follows the hovered point.
const crosshairPlugin = {
  id: 'perfHistoryCrosshair',
  afterDatasetsDraw(chart: Chart) {
    const active = chart.tooltip?.getActiveElements() ?? [];
    if (!active.length) return;
    const x = active[0].element.x;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.strokeStyle = COLOR.crosshair;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  },
};

Chart.register(...registerables, annotationPlugin, zoomPlugin, whiteBackgroundPlugin, crosshairPlugin);

interface StageBlock {
  stage_name: string;
  x0: string;
  x1: string;
}

@Component({
  selector: 'app-thematic-performance-history',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective, LoadingProgressComponent],
  templateUrl: './thematic-performance-history.component.html',
  styleUrl: './thematic-performance-history.component.css',
})
export class ThematicPerformanceHistoryComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() tab_name: string | null = null;
  @Input() query: string | null = null;
  @Input() theme_params: unknown = null;

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;
  @ViewChild('exportTarget') private exportTarget?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  data: IThematicPerformancePoint[] = [];
  chartjsData!: ChartConfiguration<'line'>['data'];
  chartjsOptions!: ChartConfiguration<'line'>['options'];

  readonly periods = ['1Y', '3Y', '10Y'] as const;
  activePeriod: typeof this.periods[number] = '10Y';
  private lastDateMs?: number;
  private fullRangeMinMs?: number;

  showChart = false;
  showSpinner = false;
  showDataMissing = false;
  dataMissingMessage: string | null = null;
  loadingFailed = false;

  readonly stageLabels = STAGE_LABELS;
  readonly LOADING_STAGES = LOADING_STAGES;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.reset();
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Public actions ──────────────────────────────────────────────

  resetZoom(): void {
    this.chartDirective?.chart?.resetZoom();
    this.activePeriod = '10Y';
  }

  setPeriod(period: typeof this.periods[number]): void {
    const chart = this.chartDirective?.chart;
    if (!chart || this.lastDateMs == null || this.fullRangeMinMs == null) return;
    this.activePeriod = period;

    if (period === '10Y') {
      chart.resetZoom();
      return;
    }

    const yearsBack = period === '1Y' ? 1 : 3;
    const from = new Date(this.lastDateMs);
    from.setFullYear(from.getFullYear() - yearsBack);
    const min = Math.max(from.getTime(), this.fullRangeMinMs);
    chart.zoomScale('x', { min, max: this.lastDateMs }, 'default');
  }

  async downloadPng(): Promise<void> {
    const node = this.exportTarget?.nativeElement;
    if (!node) return;
    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });
    const safeName = (this.tab_name || 'performance-history').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${safeName}-performance-history.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  loadData(): void {
    this.reset();
    const currentTabId = this.tabId;
    this.showSpinner = true;
    const p = (this.theme_params as any)?.params ?? {};

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, SIDENAV_KEY)).pipe(
      take(1),
      switchMap((cached) => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.data = cached;
            this.buildChart();
            this.showSpinner = false;
          }
          return of(null);
        }
        return this.apiService.getThematicPerformanceHistory({
          cluster_id: p.cluster_id ?? null,
          cluster_name: p.cluster_name ?? null,
          query: this.query,
          theme_search_type: p.theme_search_type ?? 'cluster',
          tabId: currentTabId,
        }).pipe(
          takeUntil(this.destroy$),
          tap((res) => {
            const payload = res.body?.payload;
            const data = payload?.data ?? [];
            if (!res.ok || !data.length) {
              if (this.tabId === currentTabId) {
                this.showSpinner = false;
                this.showDataMissing = true;
                this.dataMissingMessage = res.body?.message ?? null;
              }
              return;
            }
            const targetTabId = payload?.tabId ?? currentTabId;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: SIDENAV_KEY, data }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.showSpinner = false;
              this.data = data;
              this.buildChart();
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.showSpinner = false;
          this.loadingFailed = true;
        }
      },
    });
  }

  // ─── Chart construction ──────────────────────────────────────────

  private reset(): void {
    this.data = [];
    this.showChart = false;
    this.showSpinner = false;
    this.showDataMissing = false;
    this.dataMissingMessage = null;
    this.loadingFailed = false;
  }

  private buildChart(): void {
    if (!this.data.length) {
      this.showDataMissing = true;
      return;
    }

    const sorted = [...this.data].sort((a, b) => a.date_bom.localeCompare(b.date_bom));
    const values = sorted.map(p => p.cumulative_return_indexed);

    // Enforce 10-year x-axis minimum, then pad both sides so labels / last point aren't clipped.
    const lastDate = new Date(sorted[sorted.length - 1].date_eom);
    const tenYrsAgo = new Date(lastDate);
    tenYrsAgo.setFullYear(tenYrsAgo.getFullYear() - 10);
    const firstDate = new Date(sorted[0].date_eom);
    const rangeStart = firstDate < tenYrsAgo ? firstDate : tenYrsAgo;
    const rangeStartStr = isoDay(rangeStart);
    const xMinStr = isoDay(addDays(rangeStart, -AXIS_PAD_DAYS));
    const xMaxStr = isoDay(addDays(lastDate, AXIS_PAD_DAYS));

    // Prepend a "No Stage" band if the 10-year window starts before data exists.
    const stageBlocks = this.buildStageBlocks(sorted);
    const firstDataDate = sorted[0].date_bom;
    if (rangeStartStr < firstDataDate) {
      stageBlocks.unshift({ stage_name: 'No Stage', x0: rangeStartStr, x1: firstDataDate });
    }

    this.chartjsData = this.buildChartData(sorted);
    this.chartjsOptions = this.buildChartOptions(values, stageBlocks, xMinStr, xMaxStr);
    this.lastDateMs = lastDate.getTime();
    this.fullRangeMinMs = new Date(xMinStr).getTime();
    this.activePeriod = '10Y';
    this.showChart = true;
  }

  private buildChartData(sorted: IThematicPerformancePoint[]): ChartConfiguration<'line'>['data'] {
    return {
      datasets: [{
        label: 'Cumulative Return',
        data: sorted.map(p => ({ x: p.date_eom, y: p.cumulative_return_indexed })) as any,
        borderColor: COLOR.line,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: COLOR.line,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        fill: false,
        cubicInterpolationMode: 'monotone',
        tension: 0.35,
      }],
    };
  }

  private buildChartOptions(
    values: number[],
    stageBlocks: StageBlock[],
    xMinStr: string,
    xMaxStr: string,
  ): ChartConfiguration<'line'>['options'] {
    // Tight fit to the data: pad ±40 around min/max, round both ends to nice tens.
    const minY = Math.floor((Math.min(...values) - 40) / 10) * 10;
    const maxY = Math.ceil((Math.max(...values) + 40) / 10) * 10;

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 24, right: 32, bottom: 8, left: 8 } },
      animation: {
        duration: 900,
        easing: 'easeOutQuart',
      },
      animations: {
        y: {
          type: 'number',
          duration: 900,
          easing: 'easeOutQuart',
          from: (ctx: any) => {
            if (ctx.type !== 'data' || ctx.mode !== 'default') return undefined;
            return ctx.chart.scales['y']?.getPixelForValue(minY);
          },
        },
        x: { duration: 0 },
      },
      transitions: {
        active: { animation: { duration: 0 } },
      },
      plugins: {
        legend: { display: false },
        tooltip: this.tooltipConfig(),
        annotation: { annotations: this.buildStageAnnotations(stageBlocks) },
        zoom: this.zoomConfig(xMinStr, xMaxStr),
      },
      scales: {
        x: this.xScaleConfig(xMinStr, xMaxStr),
        y: this.yScaleConfig(minY, maxY),
      },
    };
  }

  private buildStageAnnotations(stageBlocks: StageBlock[]): Record<string, any> {
    const annotations: Record<string, any> = {};
    stageBlocks.forEach((block, i) => {
      const stage = block.stage_name as StageName;
      annotations[`stage_bg_${i}`] = {
        type: 'box',
        xMin: block.x0,
        xMax: block.x1,
        yMin: 'start',
        yMax: 'end',
        backgroundColor: STAGE_SOFT_BG[stage] ?? STAGE_SOFT_BG['No Stage'],
        borderWidth: 0,
        drawTime: 'beforeDatasetsDraw',
        label: {
          display: true,
          content: stage,
          position: { x: 'start', y: 'start' },
          xAdjust: 8,
          yAdjust: 10,
          color: STAGE_COLORS[stage] ?? STAGE_COLORS['No Stage'],
          font: { family: FONT_FAMILY, size: 13, weight: 600 },
          backgroundColor: 'transparent',
          padding: 0,
        },
      };
    });
    return annotations;
  }

  private tooltipConfig(): any {
    return {
      backgroundColor: COLOR.tooltipBg,
      titleColor: COLOR.tooltipTitle,
      titleFont: { family: FONT_FAMILY, size: 13, weight: 500 },
      bodyColor: COLOR.tooltipBody,
      bodyFont: { family: FONT_FAMILY, size: 15, weight: 600 },
      padding: 12,
      borderColor: COLOR.tooltipBorder,
      borderWidth: 1,
      displayColors: false,
      cornerRadius: 8,
      caretSize: 0,
      callbacks: {
        title: (items: any[]) => {
          const x = items[0].parsed.x ?? 0;
          return new Date(x).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        },
        label: (item: any) => `${(item.parsed.y ?? 0).toFixed(1)}`,
      },
    };
  }

  private zoomConfig(xMinStr: string, xMaxStr: string): any {
    return {
      limits: {
        x: {
          min: new Date(xMinStr).getTime(),
          max: new Date(xMaxStr).getTime(),
          minRange: MIN_ZOOM_RANGE_MS,
        },
      },
      pan: {
        enabled: true,
        mode: 'x',
        modifierKey: 'shift', // drag alone is reserved for box-zoom
      },
      zoom: {
        wheel: { enabled: false },
        pinch: { enabled: true },
        drag: {
          enabled: true,
          backgroundColor: COLOR.zoomBoxBg,
          borderColor: COLOR.zoomBoxBorder,
          borderWidth: 1,
        },
        mode: 'x',
      },
    };
  }

  private xScaleConfig(xMinStr: string, xMaxStr: string): any {
    return {
      type: 'time',
      min: xMinStr,
      max: xMaxStr,
      time: { unit: 'year', tooltipFormat: 'MMM yyyy', displayFormats: { year: 'yyyy' } },
      ticks: {
        maxRotation: 0,
        font: { family: FONT_FAMILY, size: 14 },
        color: COLOR.axisText,
        padding: 8,
      },
      grid: { display: false },
      border: { display: false },
    };
  }

  private yScaleConfig(minY: number, maxY: number): any {
    return {
      min: minY,
      max: maxY,
      position: 'left',
      title: {
        display: true,
        text: 'Cumulative Return (Indexed, base = 100)',
        font: { family: FONT_FAMILY, size: 14, weight: 500 },
        color: COLOR.axisTitle,
        padding: { bottom: 8 },
      },
      ticks: {
        maxTicksLimit: 6, // Chart.js picks a clean step size that yields ~6 ticks
        font: { family: FONT_FAMILY, size: 14 },
        color: COLOR.axisText,
        padding: 8,
        callback: (v: number) => `${v}`,
      },
      grid: { color: COLOR.grid },
      border: { display: false },
    };
  }

  private buildStageBlocks(sorted: IThematicPerformancePoint[]): StageBlock[] {
    const blocks: StageBlock[] = [];
    let current: StageBlock = { stage_name: sorted[0].stage_name, x0: sorted[0].date_bom, x1: sorted[0].date_eom };

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].stage_name === current.stage_name) {
        current.x1 = sorted[i].date_eom;
      } else {
        blocks.push({ ...current });
        current = { stage_name: sorted[i].stage_name, x0: sorted[i].date_bom, x1: sorted[i].date_eom };
      }
    }
    blocks.push(current);
    return blocks;
  }
}
